import { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { ChevronRight, ExpandMoreRounded } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TranslateIcon from '@mui/icons-material/Translate';
import { NavBarProps, NavGroupIcon, NavModule, NavSubmodule } from './types';
import { UNGROUPED_MODULE_ICON } from './navConfig';
import { isNewTabClick, toStandaloneModule } from './sideNavUtils';
import pvxLogo from '@assets/icons/pvx-logo.svg';
import MenuIcon from '@mui/icons-material/Menu';
import UserLogoutPopover from './components/UserLogoutPopover';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import BannerCarousel from '../Banner/BannerCarousel';
import TruncatingTypography from '../TruncatingTypography/TruncatingTypography';

const MobileNavBar: React.FC<NavBarProps> = ({
  navGroups,
  ungroupedModules,
  openKeys,
  onToggleGroup,
  onModuleClick,
  onSubmoduleClick,
  isGroupActive,
  isModuleActive,
  isSubmoduleActive,
  userName,
  onClickLogout,
  customWidth,
  activeBanners,
}) => {
  const theme = useTheme();
  const { t: translation } = useTranslation();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const isLanguageSelectorOpen = Boolean(languageSelectorAnchorEl);

  const handleMobileMenuToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLinkClick = (event: React.MouseEvent, submodule: NavSubmodule) => {
    if (isNewTabClick(event)) return;
    event.preventDefault();
    onSubmoduleClick(submodule);
    setIsMobileOpen(false);
  };

  const getTopLevelRowSx = (isActive: boolean) => ({
    width: '100%',
    color: isActive ? '#162C36' : '#64748B',
    textDecoration: 'none',
    borderRadius: '8px',
    backgroundColor: isActive ? '#FAFBFC' : '#FFFFFF',
    px: 1.5,
    '&:hover': {
      color: '#162C36',
      backgroundColor: '#FAFBFC',
    },
  });

  const renderTopLevelContent = (Icon: NavGroupIcon, label: string, isOpen = true) => (
    <>
      <Icon
        style={{
          marginRight: '0.5rem',
          flexShrink: 0,
          height: '18px',
          width: '18px',
        }}
      />

      <TruncatingTypography sx={{ flex: 1, minWidth: 0 }} fontWeight={400} fontSize="1rem">
        {label}
      </TruncatingTypography>

      {isOpen ? <></> : <ChevronRight sx={{ fontSize: 20, flexShrink: 0 }} />}
    </>
  );

  const renderModule = (module: NavModule) => {
    const isNested = module.nested;
    // Text highlights the module path; the background marks only the open page
    const isActive = isModuleActive(module);
    const isModuleOpen = isNested && openKeys.includes(module.key);

    const rowSx = {
      width: '100%',
      color: isActive ? '#162C36' : '#64748B',
      textDecoration: 'none',
      backgroundColor: isActive ? '#F5F7F8' : '#FFFFFF',
      borderRadius: '8px',
      py: 0,
      mb: '0.5rem',
      '&:hover': {
        color: '#162C36',
        backgroundColor: '#F5F7F8',
      },
    };

    const rowContent = (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <TruncatingTypography
            sx={{ flex: 1, minWidth: 0, my: 0.5 }}
            fontWeight={!isNested && isActive ? 600 : 400}
            fontSize="1rem"
          >
            {module.label}
          </TruncatingTypography>
        </Box>

        {isNested &&
          (isModuleOpen ? (
            <ExpandMoreRounded sx={{ fontSize: 20, flexShrink: 0 }} />
          ) : (
            <ChevronRight sx={{ fontSize: 20, flexShrink: 0 }} />
          ))}
      </>
    );

    return (
      <Box key={module.key} sx={{ marginLeft: 1 }}>
        {isNested ? (
          <ListItem
            onClick={() => {
              if (onModuleClick(module)) setIsMobileOpen(false);
            }}
            sx={rowSx}
          >
            {rowContent}
          </ListItem>
        ) : (
          <ListItem
            component="a"
            href={module.submodules[0].href}
            onClick={(event) => handleLinkClick(event, module.submodules[0])}
            sx={rowSx}
          >
            {rowContent}
          </ListItem>
        )}

        {/* LEVEL 3: SUBMODULES */}
        {isModuleOpen && (
          <List disablePadding sx={{ ml: 3, borderLeft: '1px solid #EBEEF1', pl: '0.25rem' }}>
            {module.submodules.map((submodule) => {
              const isCurrentSubmodule = isSubmoduleActive(submodule);

              return (
                <ListItem
                  key={submodule.href}
                  component="a"
                  href={submodule.href}
                  onClick={(event) => handleLinkClick(event, submodule)}
                  sx={{
                    width: '95%',
                    color: isCurrentSubmodule ? '#162C36' : '#64748B',
                    textDecoration: 'none',
                    backgroundColor: isCurrentSubmodule ? '#F5F7F8' : '#FFFFFF',
                    borderRadius: '8px',
                    mb: 0.5,
                    py: 0,
                    px: '8px',
                    '&:hover': {
                      color: '#162C36',
                      backgroundColor: '#F5F7F8',
                    },
                  }}
                >
                  <TruncatingTypography
                    sx={{ flex: 1, minWidth: 0, my: 0.5 }}
                    fontWeight={isCurrentSubmodule ? 600 : 400}
                    fontFamily="Manrope, sans-serif"
                    fontSize="1rem"
                  >
                    {submodule.label}
                  </TruncatingTypography>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    );
  };

  // Top-level row that expands its modules: a group, or an ungrouped nested module
  const renderExpandableRow = (
    key: string,
    Icon: NavGroupIcon,
    label: string,
    isActive: boolean,
    modules: NavModule[],
  ) => {
    const isOpen = openKeys.includes(key);

    return (
      <Box key={key}>
        {/* LEVEL 1: GROUP */}
        <ListItem onClick={() => onToggleGroup(key)} sx={getTopLevelRowSx(isActive)}>
          {renderTopLevelContent(Icon, label, isOpen)}
        </ListItem>

        {/* LEVEL 2: MODULES AND STANDALONE SUBMODULES */}
        {isOpen && (
          <List disablePadding sx={{ ml: 2.5, mr: 2, borderLeft: '1px solid #EBEEF1' }}>
            {modules.map(renderModule)}
          </List>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' }, height: '40px', zIndex: 1000 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 2,
            width: '100%',
            background: '#FFFFFF',
            padding: '16px 24px',
            alignItems: 'center',
            borderBottom: '1px solid #EBEEF1',
          }}
        >
          {navGroups.length || ungroupedModules.length ? (
            <IconButton onClick={handleMobileMenuToggle} sx={{ justifySelf: 'start' }}>
              <MenuIcon sx={{ color: '#090D11' }} />
            </IconButton>
          ) : (
            <></>
          )}
          <Box
            sx={{
              gridColumn: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <img
              src={pvxLogo}
              alt="PvX"
              style={{
                height: '24px',
                width: '24px',
              }}
            />

            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '20px',
                lineHeight: '22px',
                color: '#090D11',
              }}
            >
              PvX Partners
            </Typography>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={isMobileOpen}
        onClose={handleMobileMenuToggle}
        fullScreen
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: customWidth,
          maxWidth: isMobileOpen ? '256px' : '336px',
        }}
        PaperProps={{
          style: {
            background: '#FFFFFF',
            color: '#090D11',
            borderRadius: '1.5rem',
          },
        }}
      >
        <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <List sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {navGroups.map((group) =>
                renderExpandableRow(
                  group.key,
                  group.icon,
                  group.label,
                  isGroupActive(group),
                  group.modules,
                ),
              )}

              {/* Demo modules and backend modules missing from NAVIGATION_CONFIG */}
              {ungroupedModules.map((module) =>
                module.nested ? (
                  renderExpandableRow(
                    module.key,
                    UNGROUPED_MODULE_ICON,
                    module.label,
                    isModuleActive(module),
                    module.submodules.map(toStandaloneModule),
                  )
                ) : (
                  <ListItem
                    key={module.key}
                    component="a"
                    href={module.submodules[0].href}
                    onClick={(event) => handleLinkClick(event, module.submodules[0])}
                    sx={getTopLevelRowSx(isModuleActive(module))}
                  >
                    {renderTopLevelContent(UNGROUPED_MODULE_ICON, module.label)}
                  </ListItem>
                ),
              )}
            </List>
            {activeBanners && activeBanners.length > 0 && (
              <Box sx={{ marginTop: 'auto', flexShrink: 0 }}>
                <BannerCarousel banners={activeBanners} />
              </Box>
            )}
          </Box>
          <Box sx={{ width: '100%', flexShrink: 0, mt: '20px' }}>
            <Box
              sx={{ display: 'flex', gap: '0.5rem', padding: '0.5rem', flexDirection: 'column' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingLeft: '0.5rem',
                  py: 0,
                  borderRadius: '8px',
                  backgroundColor: isLanguageSelectorOpen ? '#F5F7F8' : 'transparent',
                  color: isLanguageSelectorOpen ? '#162C36' : '#64748B',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#F5F7F8',
                    color: '#162C36',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <TranslateIcon sx={{ fontSize: '1.25rem', color: '#64748B' }} />
                  <Typography
                    fontSize="0.85rem"
                    fontWeight={isLanguageSelectorOpen ? 600 : 400}
                    lineHeight="1.25rem"
                    sx={{ color: isLanguageSelectorOpen ? '#162C36' : '#64748B' }}
                  >
                    {translation('signup.language')}
                  </Typography>
                </Box>

                <LanguageSelector
                  anchorEl={languageSelectorAnchorEl}
                  setAnchorEl={setLanguageSelectorAnchorEl}
                />
              </Box>
            </Box>
            {userName && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#FFFFFF',
                    borderTop: '1px solid #EBEEF1',
                  }}
                >
                  <ListItemText
                    primary={userName}
                    primaryTypographyProps={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      color: '#090D11',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '140px',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 24,
                      height: 24,
                      marginLeft: 'auto',
                      cursor: 'pointer',
                    }}
                    onClick={(event: any) => {
                      setAnchorEl(event.currentTarget as HTMLElement);
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: '16px', color: '#090D11' }} />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {anchorEl && (
        <UserLogoutPopover
          handlePopoverClose={() => setAnchorEl(null)}
          logoutHandler={onClickLogout}
          anchorEl={anchorEl}
        />
      )}
    </>
  );
};

export default MobileNavBar;
